import { prependZero } from 'utils/string';
import { getLocalDate } from 'utils/date';

export default class Countdown {
  /**
   * @constructor
   * @param {any}     element - HTML Node
   *
   * @param {Object}  settings - Custom settings
   * @param {string}  settings.endDate - String as date
   * @param {string}  [settings.splitCharacter] - Character splitting datestring
   * @param {number}  [settings.interval] - Interval between executions
   * @param {boolean} [settings.reloadAfterEnd] - Reload after date end
   *
   * @param {Object}  [settings.selectors] - Queryselectors
   * @param {string}  [settings.selectors.days] - Days selector
   * @param {string}  [settings.selectors.hours] - Hours selector
   * @param {string}  [settings.selectors.minutes] - Minutes selector
   * @param {string}  [settings.selectors.seconds] - Seconds selector
   */

  constructor(element, settings = {}) {
    this.element = element;
    this.endDate = '';
    this.options = { ...Countdown.defaults(), ...settings };

    this.days = this.element.querySelector(this.options.selectors.days);
    this.hours = this.element.querySelector(this.options.selectors.hours);
    this.minutes = this.element.querySelector(this.options.selectors.minutes);
    this.seconds = this.element.querySelector(this.options.selectors.seconds);

    this.setDate();

    // Bail if no time left
    if (this.getRemainingTime().difference < 0) return;

    this.timerInterval = setInterval(this.updateTimer.bind(this), this.options.interval);

    // Wait until first interval has run
    setTimeout(() => {
      this.element.classList.remove('invisible');
    }, this.options.interval);
  }

  /**
   * Default settings
   * @returns settings
   * @static
   */

  static defaults() {
    return {
      endDate: '',
      splitCharacter: '_',
      interval: 1000,
      doubleDigits: true,
      reloadAfterEnd: true,
      selectors: {
        days: '[data-days]',
        hours: '[data-hours]',
        minutes: '[data-minutes]',
        seconds: '[data-seconds]',
      },
    };
  }

  setDate() {
    const date = getLocalDate(this.options.endDate, this.options.splitCharacter);
    this.endDate = date;
  }

  updateTimer() {
    const timers = this.getRemainingTime();
    const prepend = this.options.doubleDigits;

    const {
      days, hours, minutes, seconds,
    } = timers;

    this.updateTranslation(timers);

    this.days.textContent = prepend ? prependZero(Math.floor(days)) : Math.floor(days);
    this.hours.textContent = prepend ? prependZero(Math.floor(hours)) : Math.floor(hours);
    this.minutes.textContent = prepend ? prependZero(Math.floor(minutes)) : Math.floor(minutes);
    this.seconds.textContent = prepend ? prependZero(Math.floor(seconds)) : Math.floor(seconds);

    if (timers.difference <= 0) {
      clearInterval(this.timerInterval);
      if (this.options.reloadAfterEnd) window.location.reload();
    }
  }

  updateTranslation({
    days, hours, minutes, seconds,
  }) {
    if (Math.floor(days) === 1) {
      this.days.parentNode.querySelector('[data-plural]').classList.add('hidden');
      this.days.parentNode.querySelector('[data-singular]').classList.remove('hidden');
    } else {
      this.days.parentNode.querySelector('[data-plural]').classList.remove('hidden');
      this.days.parentNode.querySelector('[data-singular]').classList.add('hidden');
    }

    if (Math.floor(hours) === 1) {
      this.hours.parentNode.querySelector('[data-plural]').classList.add('hidden');
      this.hours.parentNode.querySelector('[data-singular]').classList.remove('hidden');
    } else {
      this.hours.parentNode.querySelector('[data-plural]').classList.remove('hidden');
      this.hours.parentNode.querySelector('[data-singular]').classList.add('hidden');
    }

    if (Math.floor(minutes) === 1) {
      this.minutes.parentNode.querySelector('[data-plural]').classList.add('hidden');
      this.minutes.parentNode.querySelector('[data-singular]').classList.remove('hidden');
    } else {
      this.minutes.parentNode.querySelector('[data-plural]').classList.remove('hidden');
      this.minutes.parentNode.querySelector('[data-singular]').classList.add('hidden');
    }

    if (Math.floor(seconds) === 1) {
      this.seconds.parentNode.querySelector('[data-plural]').classList.add('hidden');
      this.seconds.parentNode.querySelector('[data-singular]').classList.remove('hidden');
    } else {
      this.seconds.parentNode.querySelector('[data-plural]').classList.remove('hidden');
      this.seconds.parentNode.querySelector('[data-singular]').classList.add('hidden');
    }
  }

  getRemainingTime() {
    const difference = this.endDate - new Date();
    let timeRemaining = Math.round(Number(difference / 1000));

    const days = Number(timeRemaining / 86400);
    timeRemaining %= 86400;

    const hours = Number(timeRemaining / 3600);
    timeRemaining %= 3600;

    const minutes = Number(timeRemaining / 60);
    timeRemaining %= 60;

    const seconds = Number(timeRemaining);

    return {
      difference,
      days,
      hours,
      minutes,
      seconds,
    };
  }
}
